import React, { FC, useCallback, useEffect } from "react";

import UserList from "../components/User/UserList";

import SettingsOverlay from '../components/User/UserSettings/SettingsOvererlay/SettingsOverlay';

import { UserPermissions, formatUserDisplayName, permissionAllowed } from "@ews/react-data";
import Icon from "@ews/react-icons";
import { useL10n } from "@ews/react-localization-context";
import { FilterState } from "../components/FilterList/types";
import Layout from '../components/Layout/User';
import { useMyPermissions } from "../components/Login/LoginProvider";
import { useNamedRoutes } from "../components/NamedRoutes";
import NavbarMain from '../components/Navigation/Navbar/Main';
import NavbarSettings from "../components/Navigation/Navbar/Settings";
import SidebarMain from '../components/Navigation/Sidebar/Main';
import { MenuLink } from "../components/Navigation/Sidebar/Sidebar";
import StatusbarMain from '../components/Navigation/Status/Main';
import { MenuToggleProps } from "../components/Navigation/types";
import { usePersistentState } from "../components/Persistence";
import { useUser } from "../components/ReactData/user";
import NewUserModal from "../components/User/NewUser/NewUserModal";
import SentNotificationsLog from "../components/User/SentNotificationsLog/SentNotificationsLog";
import { ReactClickEvent } from "../components/types";
import { setTitle } from "../utils";

type UserProps = {
  filterByCustomer?: boolean;
};

const UserSettings: React.FC = () =>
{

  const { params } = useNamedRoutes();
  const { id } = params();
  const user = useUser(id!);
  const { translate: t } = useL10n();
  setTitle(`${t('TD_USERNAME')}`, `${formatUserDisplayName(user.firstName, user.lastName, user.username)}`);

  return <>

    <Layout backbutton={true} user={user} navbar={NavbarSettings} sidebar={SidebarMain} id="user:settings">
      <SettingsOverlay user={user} />
    </Layout>
  </>;

};

const SidebarWrapper: FC<MenuToggleProps> = ({ onMenuToggle }) =>
{
  const { translate: t } = useL10n();
  const permissions: UserPermissions = useMyPermissions();


  return (<SidebarMain onMenuToggle={onMenuToggle}>
    {permissionAllowed(permissions.createUser!) ? <>
      <MenuLink
        title={t('COM_FACPMANAGEMENT_ACTION_TITLE_NEWUSER')}
        id="newUser"
        icon={<Icon icon="users_create" />}
      />
      <NewUserModal onDismiss={(e) => onMenuToggle && onMenuToggle(e as unknown as ReactClickEvent)} trigger={"newUser"} />
    </> : <></>}
  </SidebarMain>);
};



const User: React.FC<UserProps> = ({ filterByCustomer }) =>
{

  const { params } = useNamedRoutes();
  const { id: customerId } = params();
  //const result = useMyUsers({});
  const [FilterState, setFilterState] = usePersistentState<FilterState>('system-page:filter', {} as FilterState);

  const onIsOpen = useCallback((state: boolean, type: "detail" | "main") =>
  {

    console.warn("onIsOpen", state, type);
    setFilterState((pre) =>
    {
      const obj: Partial<Record<keyof FilterState, boolean>> = {};

      if (type === "detail") {
        obj.isDetailOpen = state;
      }
      else {
        obj.isFilterOpen = state;
      }

      return { ...pre, ...obj };

    });

  }
    , [setFilterState]);


  const onFilterOpen = useCallback((isFilterOpen: boolean,) =>
  {
    setFilterState((pre) => ({ ...pre, isFilterOpen }));
  }, [setFilterState]);

  const onFilterIsActive = useCallback((isFilterActive: boolean,) =>
  {
    if (FilterState.isFilterActive !== isFilterActive) {
      setFilterState((pre) => ({ ...pre, isFilterActive }));
    }
  }, [FilterState.isFilterActive, setFilterState]);

  const Bar = () =>
  {
    return <>
      <StatusbarMain
        filter
        isFilterOpen={FilterState.isFilterOpen}
        activeFilter={FilterState.isFilterActive}
        onFilterOpen={onFilterOpen}
      />
    </>;
  };

  return (
    <>
      <Layout header={false} statusbar={Bar} navbar={NavbarMain} sidebar={SidebarWrapper} id="users">
        <UserList
          customerId={filterByCustomer ? customerId : undefined}
          filter={FilterState}
          onIsOpen={onIsOpen}
          onFilterActive={onFilterIsActive}
        />
      </Layout>
    </>
  );
};

const SentNotificationsLogOverlay: React.FC = () =>
{
  const { params } = useNamedRoutes();
  const { id } = params();

  const [FilterState, setFilterState] = usePersistentState<FilterState>('system-page:filter', {} as FilterState);

  const onIsOpen = useCallback((state: boolean, type: "detail" | "main") =>
  {
    setFilterState((pre) =>
    {
      const obj: Partial<Record<keyof FilterState, boolean>> = {};

      if (type === "detail") {
        obj.isDetailOpen = state;
      }
      else {
        obj.isFilterOpen = state;
      }

      return { ...pre, ...obj };

    });

  }

    , [setFilterState]);

  const onFilterOpen = useCallback((isFilterOpen: boolean,) =>
  {
    setFilterState((pre) => ({ ...pre, isFilterOpen }));
  }, [setFilterState]);

  const onFilterIsActive = useCallback((isFilterActive: boolean,) =>
  {
    if (FilterState.isFilterActive !== isFilterActive) {
      setFilterState((pre) => ({ ...pre, isFilterActive }));
    }
  }, [FilterState?.isFilterActive, setFilterState]);

  const StatusbarFilterButton = () =>
  {
    return <>
      <StatusbarMain
        filter
        isFilterOpen={FilterState.isFilterOpen}
        activeFilter={FilterState.isFilterActive}
        onFilterOpen={onFilterOpen}
      />
    </>;
  };

  const user = useUser(id || "");
  const { translate: t } = useL10n();

  setTitle(`${t('[REACT_SEND_NOTIFICATION]')}`);

  return (
    <Layout backbutton={true} user={user} statusbar={StatusbarFilterButton} sidebar={SidebarWrapper} navbar={NavbarMain} id="user:sent-notifications-log">
      <SentNotificationsLog
        filter={FilterState}
        onIsOpen={onIsOpen}
        onFilterActive={onFilterIsActive}
        userId={id}
      />
    </Layout>
  );
};

export
{
  SentNotificationsLogOverlay as SentNotifications, User,
  UserSettings
};
